import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, ArrowRight, ChatCircleDots, Bell, UserPlus, CalendarPlus, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import AttachmentsButton from "@/components/AttachmentsButton";
import { usePermissions } from "@/lib/permissions";

const STAGES = [
  { key: "new", label: "New Lead", tone: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30" },
  { key: "contacted", label: "Contacted", tone: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/30" },
  { key: "quotation_sent", label: "Quotation Sent", tone: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  { key: "negotiation", label: "Negotiation", tone: "bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/30" },
  { key: "won", label: "Won", tone: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" },
  { key: "lost", label: "Lost", tone: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30" },
];

function LeadDetail({ lead, onClose, onChanged }) {
  const [remark, setRemark] = useState("");
  const [reminder, setReminder] = useState(lead?.reminder_at ? lead.reminder_at.slice(0, 16) : "");
  const [fuOpen, setFuOpen] = useState(false);
  const [fuDate, setFuDate] = useState("");
  const [fuNotes, setFuNotes] = useState("");
  const [fuChannel, setFuChannel] = useState("call");
  const [convOpen, setConvOpen] = useState(false);
  const [conv, setConv] = useState({ company_name: "", email: "", address: "", city: "", state: "" });

  const addRemark = async () => {
    if (!remark.trim()) return;
    try { await api.post(`/leads/${lead.id}/remark`, { text: remark });
      toast.success("Remark added"); setRemark(""); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveReminder = async () => {
    try {
      const iso = reminder ? new Date(reminder).toISOString() : null;
      await api.post(`/leads/${lead.id}/reminder`, { reminder_at: iso });
      toast.success(iso ? "Reminder set" : "Reminder cleared"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const scheduleFollowup = async () => {
    if (!fuDate) return toast.error("Pick a date");
    try {
      await api.post("/followups", {
        customer_id: lead.customer_id || null, customer_name: lead.customer_name,
        lead_id: lead.id, next_date: fuDate, notes: fuNotes, channel: fuChannel, status: "pending",
      });
      toast.success("Follow-up scheduled"); setFuOpen(false); setFuDate(""); setFuNotes("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const convert = async () => {
    try {
      const r = await api.post(`/leads/${lead.id}/convert`, conv);
      toast.success("Converted — customer ready for orders & quotes");
      setConvOpen(false); onChanged(); onClose();
      return r.data.customer_id;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remarks = [...(lead.remarks || [])].reverse();
  return (
    <Dialog open={!!lead} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{lead.customer_name}
            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary uppercase">{lead.stage.replace("_"," ")}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><div className="overline text-muted-foreground">Value</div><div className="font-heading font-bold text-lg">₹{(lead.value||0).toLocaleString("en-IN")}</div></div>
            <div><div className="overline text-muted-foreground">Priority</div><div className="capitalize font-medium">{lead.priority}</div></div>
            <div><div className="overline text-muted-foreground">Interest</div><div className="truncate">{lead.product_interest || "—"}</div></div>
          </div>
          <div className="flex flex-wrap gap-2 pt-2 border-t border-border/60">
            <Button size="sm" onClick={() => setFuOpen(true)} data-testid="lead-followup-btn"><CalendarPlus size={14} className="mr-1.5" />Schedule follow-up</Button>
            {lead.stage !== "won" && (
              <Button size="sm" variant="outline" onClick={() => setConvOpen(true)} data-testid="lead-convert-btn"><UserPlus size={14} className="mr-1.5" />Convert to customer</Button>
            )}
          </div>
          <div>
            <div className="overline text-muted-foreground mb-2 flex items-center gap-1.5"><Bell size={12} />Reminder</div>
            <div className="flex gap-2">
              <Input type="datetime-local" value={reminder} onChange={(e) => setReminder(e.target.value)} data-testid="lead-reminder-input" />
              <Button size="sm" onClick={saveReminder} data-testid="lead-reminder-save">Save</Button>
            </div>
            {lead.reminder_at && !lead.reminder_notified && <p className="text-[11px] text-muted-foreground mt-1"><Clock size={10} className="inline mr-1" />Will notify at {new Date(lead.reminder_at).toLocaleString("en-IN")}</p>}
          </div>
          <div>
            <div className="overline text-muted-foreground mb-2 flex items-center gap-1.5"><ChatCircleDots size={12} />Remarks & history ({remarks.length})</div>
            <div className="flex gap-2 mb-3">
              <Textarea rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Add a remark…" className="flex-1" data-testid="lead-remark-input" />
              <Button size="sm" onClick={addRemark} data-testid="lead-remark-save">Add</Button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {remarks.length === 0 && <div className="text-xs text-muted-foreground italic py-4 text-center">No remarks yet — the first note starts the trail.</div>}
              {remarks.map((r, i) => (
                <div key={i} className="text-sm border-l-2 border-primary/40 pl-3 py-1" data-testid={`lead-remark-${i}`}>
                  <div>{r.text}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{r.by_name} · {new Date(r.at).toLocaleString("en-IN")}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>

      <Dialog open={fuOpen} onOpenChange={setFuOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Schedule follow-up · {lead.customer_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">DATE</Label><Input type="date" value={fuDate} onChange={(e) => setFuDate(e.target.value)} data-testid="fu-date" /></div>
            <div><Label className="text-xs">CHANNEL</Label>
              <Select value={fuChannel} onValueChange={setFuChannel}>
                <SelectTrigger data-testid="fu-channel"><SelectValue /></SelectTrigger>
                <SelectContent>{["call","whatsapp","email","meeting"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">NOTES</Label><Textarea rows={3} value={fuNotes} onChange={(e) => setFuNotes(e.target.value)} data-testid="fu-notes" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setFuOpen(false)}>Cancel</Button><Button onClick={scheduleFollowup} data-testid="fu-save">Schedule</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={convOpen} onOpenChange={setConvOpen}>
        <DialogContent className="max-w-md" data-testid="lead-convert-dialog">
          <DialogHeader><DialogTitle>Convert to customer</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-muted-foreground">Extra details are optional — customer becomes available for Orders & Quotations immediately.</p>
            <div><Label className="text-xs">COMPANY / FIRM</Label><Input value={conv.company_name} onChange={(e) => setConv({...conv, company_name: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">EMAIL</Label><Input value={conv.email} onChange={(e) => setConv({...conv, email: e.target.value})} /></div>
              <div><Label className="text-xs">CITY</Label><Input value={conv.city} onChange={(e) => setConv({...conv, city: e.target.value})} /></div>
            </div>
            <div><Label className="text-xs">ADDRESS</Label><Textarea rows={2} value={conv.address} onChange={(e) => setConv({...conv, address: e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setConvOpen(false)}>Cancel</Button><Button onClick={convert} data-testid="lead-convert-save">Convert & mark won</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export default function Leads() {
  const { can, canEdit } = usePermissions();
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ customer_id: "", customer_name: "", mobile: "", product_interest: "", value: 0, stage: "new", priority: "medium", source: "Website" });

  const load = async () => {
    const [l, c] = await Promise.all([api.get("/leads"),
                                      api.get("/customers", { params: { scope_mode: "tenant" } })]);
    setLeads(l.data); setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);
  // Poll reminders every 60s in-app so the user gets a bell notification without a redeploy.
  useEffect(() => {
    const tick = () => api.get("/leads/reminders/due").catch(() => {});
    tick(); const t = setInterval(tick, 60_000); return () => clearInterval(t);
  }, []);

  const advance = async (lead) => {
    const idx = STAGES.findIndex(s => s.key === lead.stage);
    if (idx >= 4) return;
    await api.put(`/leads/${lead.id}`, { stage: STAGES[Math.min(idx + 1, 4)].key });
    load();
  };
  const changeStage = async (lead, stage) => { await api.put(`/leads/${lead.id}`, { stage }); load(); };
  const create = async () => {
    if (!form.customer_name) return toast.error("Customer required");
    await api.post("/leads", { ...form, value: parseFloat(form.value) || 0 });
    toast.success("Lead created"); setOpen(false);
    setForm({ customer_id: "", customer_name: "", mobile: "", product_interest: "", value: 0, stage: "new", priority: "medium", source: "Website" });
    load();
  };
  const onCustomerPick = (id) => {
    const c = customers.find(x => x.id === id);
    if (c) setForm({ ...form, customer_id: id, customer_name: c.customer_name, mobile: c.mobile });
  };
  const openLead = async (l) => {
    // Refetch to make sure remarks are current (list endpoint may cache)
    try { const r = await api.get(`/leads/${l.id}`); setSelected(r.data); }
    catch { setSelected(l); }
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="overline text-muted-foreground mb-2">Pipeline</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">Leads</h1>
          <p className="text-sm text-muted-foreground mt-2">{leads.length} opportunities in flight · click any lead card to open history, remarks & follow-ups.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          {can("leads.create") && (
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-lead-button"><Plus size={16} className="mr-2" /> New Lead</Button>
            </DialogTrigger>
          )}
          <DialogContent>
            <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs">CUSTOMER (optional — leave blank for a fresh prospect)</Label>
                <Select value={form.customer_id} onValueChange={onCustomerPick}>
                  <SelectTrigger data-testid="lead-customer-select"><SelectValue placeholder="Pick a customer" /></SelectTrigger>
                  <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">NAME</Label><Input value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} data-testid="lead-name" /></div>
                <div><Label className="text-xs">MOBILE</Label><Input value={form.mobile} onChange={(e) => setForm({...form, mobile: e.target.value})} data-testid="lead-mobile" /></div>
              </div>
              <div><Label className="text-xs">PRODUCT INTEREST</Label><Input value={form.product_interest} onChange={(e) => setForm({...form, product_interest: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">VALUE (₹)</Label><Input type="number" value={form.value} onChange={(e) => setForm({...form, value: e.target.value})} /></div>
                <div><Label className="text-xs">PRIORITY</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["high","medium","low"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} data-testid="save-lead-button">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {STAGES.map((stage) => {
          const items = leads.filter(l => l.stage === stage.key);
          const total = items.reduce((s, x) => s + (x.value || 0), 0);
          return (
            <div key={stage.key} className="flex flex-col" data-testid={`col-${stage.key}`}>
              <div className={`p-3 rounded-t-lg border ${stage.tone}`}>
                <div className="text-xs font-semibold uppercase tracking-wider">{stage.label}</div>
                <div className="text-xs opacity-70 mt-1">{items.length} · ₹{total.toLocaleString("en-IN")}</div>
              </div>
              <div className="flex-1 p-2 space-y-2 bg-secondary/30 rounded-b-lg min-h-[200px]">
                {items.map((l) => (
                  <Card key={l.id} className="p-3 hover-lift cursor-pointer" data-testid={`lead-card-${l.id}`} onClick={() => openLead(l)}>
                    <div className="flex items-start justify-between mb-1">
                      <div className="font-medium text-sm">{l.customer_name}</div>
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${l.priority==='high'?'bg-destructive/15 text-destructive':l.priority==='low'?'bg-muted text-muted-foreground':'bg-primary/10 text-primary'}`}>{l.priority}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mb-2 line-clamp-2">{l.product_interest || "No interest set"}</div>
                    <div className="flex items-center justify-between">
                      <div className="font-heading font-bold text-sm">₹{(l.value||0).toLocaleString("en-IN")}</div>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        {l.remarks?.length > 0 && <span title="Remarks"><ChatCircleDots size={11} className="inline" /> {l.remarks.length}</span>}
                        {l.reminder_at && !l.reminder_notified && <span title="Reminder set"><Bell size={11} weight="fill" className="inline text-amber-500" /></span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
                      <AttachmentsButton entityType="lead" entityId={l.id} label={`Files — ${l.customer_name}`} testId={`attach-lead-${l.id}`} />
                      {canEdit("leads.edit", l) && stage.key !== "won" && stage.key !== "lost" && (
                        <button onClick={() => advance(l)} className="text-[10px] text-primary hover:underline flex items-center gap-0.5" data-testid={`advance-${l.id}`}>
                          Next <ArrowRight size={10} />
                        </button>
                      )}
                      {canEdit("leads.edit", l) && (
                        <Select value={l.stage} onValueChange={(v) => changeStage(l, v)}>
                          <SelectTrigger className="h-6 text-[10px] ml-auto" data-testid={`lead-stage-${l.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </div>
                  </Card>
                ))}
                {!items.length && <div className="text-xs text-muted-foreground text-center py-6">Drop a lead here</div>}
              </div>
            </div>
          );
        })}
      </div>

      {selected && <LeadDetail lead={selected} onClose={() => setSelected(null)} onChanged={() => { openLead(selected); load(); }} />}
    </div>
  );
}
